import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Orb from "@/components/originkit/ui/cosmic-orb";
import { useLanguage } from "@/i18n/LanguageProvider";

/**
 * VyvEnergy — a "energia" do VYV (decisões e19cb217 / d8c60a71, 07/08/2026).
 *
 * ÚNICA porta de entrada para o Cosmic Orb do Originkit: nenhum outro arquivo
 * importa `originkit/ui/cosmic-orb` direto. Substitui o círculo placeholder
 * (AvatarPortal, removido em 08/08) em TODAS as superfícies sem avatar —
 * no Espelho reagindo aos 4
 * estados do Presence Engine e à voz.
 *
 * Estados → envelope de áudio (0..1) entregue ao orb:
 *  - idle       → 0 (o orb tem vida própria: giro, pulsar, cometas).
 *  - listening  → `level` (o envelope que o app JÁ calcula — amplitude do
 *                 useVoiceInputWhisper) ou RMS de um `source` MediaStream.
 *  - thinking   → respiração sintética embutida (período 3.5s, o mesmo
 *                 haloBreathDuration do estado thinking no Presence Engine).
 *  - speaking   → RMS do `source` HTMLAudioElement via WebAudio QUANDO é
 *                 seguro; senão cadência de fala sintética (ver nota iOS).
 *                 Ou RMS de um `source` MediaStream REMOTO (LIVE Realtime,
 *                 LOTE 3): createMediaStreamSource NÃO reroteia a saída do
 *                 player, então roda em todo lugar, iOS incluído.
 *
 * ⚠ iOS/WebKit: `createMediaElementSource` REROTEIA a saída do elemento pelo
 * AudioContext — se o contexto suspender, o TTS fica MUDO. Foi exatamente o
 * incidente que desativou o META_PERSON_LIP_SYNC (ver bloco comentado no
 * MirrorChat). Por isso o caminho WebAudio do elemento:
 *  1. nunca roda em iOS/WebKit (fallback sintético direto);
 *  2. fora do iOS, só liga com o contexto comprovadamente "running";
 *  3. cria a fonte UMA vez por elemento (WeakMap) e mantém a conexão com
 *     ctx.destination para sempre — desconectá-la silenciaria o player.
 *
 * Paleta: ciano oficial (bg #0A0F18 exato; família do --primary hsl(187 72%)).
 * Sobrescreva via CSS vars --vyv-energy-{bg,anchor,a,b,c} — o wrapper lê com
 * fallback. O picker de paletas é FASE 2 (galeria vyv-orb-paletas.html) e NÃO
 * entra aqui.
 *
 * Performance: uma instância por tela; `lens` desliga automaticamente acima
 * de 420px de diâmetro (6 avaliações de starfield/pixel com lente vs 2 sem);
 * `prefers-reduced-motion` congela o orb num frame (patch 3) e desliga os
 * loops de análise daqui.
 *
 * Sem WebGL (desabilitado, indisponível, limite de contextos do browser
 * estourado, shader que não linka) o orb viraria um canvas VAZIO — e para o
 * usuário sem avatar a energia é a presença inteira do Espelho. Por isso:
 * sonda de WebGL uma vez por página + `onInitError` do orb (patch 4), e nos
 * dois casos um fallback CSS com a mesma paleta assume (review Codex P2,
 * PR #447).
 */

export type VyvEnergyState = "idle" | "listening" | "thinking" | "speaking";

export type VyvEnergySource = MediaStream | HTMLAudioElement | null;

interface VyvEnergyProps {
  state: VyvEnergyState;
  /** Diâmetro em px. Omitido → preenche o pai (lado menor, via ResizeObserver). */
  size?: number;
  /**
   * Fração do LADO MENOR do container ocupada pelo diâmetro (0..1]. Default 1
   * = preencher, o comportamento de sempre. Ignorado quando `size` é passado.
   *
   * Existe porque a energia entrou grande demais nas 9 superfícies migradas
   * (#449) e a correção é por PROPORÇÃO, não por pixel: metade de 200px no
   * celular e metade de 280px no desktop são números diferentes do mesmo
   * enquadramento. Ancorar no lado MENOR (e não na largura) é o que faz o
   * card do Body Map — largo no desktop, alto no celular — encolher igual nos
   * dois; uma porcentagem de largura viraria 79% do quadro no desktop.
   *
   * A centralização continua sendo do flex do root: mudar o diâmetro NÃO mexe
   * em posição. Foi exatamente a margem fixa que quebrou o enquadramento na
   * #447 quando o tamanho mudou.
   */
  ratio?: number;
  /** Fonte de áudio viva para o envelope (mic MediaStream ou <audio> do TTS). */
  source?: VyvEnergySource;
  /**
   * Envelope 0..1 já calculado pelo chamador (ex.: `amplitude` do
   * useVoiceInputWhisper). Quando presente em listening/speaking, vence o
   * `source` — evita um segundo AudioContext em cima do mesmo microfone.
   */
  level?: number;
  /**
   * Drag-to-spin com o dedo/mouse (patch 5 do orb; default true). Sempre
   * inerte com prefers-reduced-motion, independente do valor.
   */
  interactive?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Fallbacks da paleta oficial — bg exato da decisão d8c60a71; ciano #45DBE5
 * amostrado; vizinhos derivados do token real `--primary: 187 72% 50%`
 * (#24C6DB) do design system. */
const FALLBACK_PALETTE = {
  bg: "#0A0F18",
  anchor: "#146F7B",
  a: "#24C6DB",
  b: "#45DBE5",
  c: "#7FE6FF",
} as const;

const LENS_MAX_DIAMETER = 420;

/** iPadOS se apresenta como MacIntel com touch — cobrir os dois. */
const IS_IOS_WEBKIT =
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function cssVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
}

// ── Sonda de WebGL (uma vez por página) ─────────────────────────────────────
// Detecta a classe dominante de falha (WebGL desabilitado/indisponível) ANTES
// de montar o orb. Falhas tardias (limite de contextos, shader que não linka)
// chegam pelo onInitError do próprio orb.

let webglSupport: boolean | null = null;

function hasWebGL(): boolean {
  if (webglSupport !== null) return webglSupport;
  if (typeof document === "undefined") return false;
  try {
    const c = document.createElement("canvas");
    const gl = (c.getContext("webgl") ||
      c.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    // Libera o contexto da sonda na hora — não gastar um slot do browser.
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
    webglSupport = !!gl;
  } catch {
    webglSupport = false;
  }
  return webglSupport;
}

// ── WebAudio compartilhado ──────────────────────────────────────────────────
// Um único AudioContext para todas as instâncias/montagens da energia. Nunca
// é fechado: MediaElementSourceNodes criados nele precisam do destination
// vivo enquanto o elemento existir.

let sharedCtx: AudioContext | null = null;

function getSharedCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AC: typeof AudioContext | undefined =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AC) return null;
  if (!sharedCtx || sharedCtx.state === "closed") sharedCtx = new AC();
  return sharedCtx;
}

const RESUME_EVENTS = ["pointerdown", "touchend", "keydown"] as const;
let resumeArmed = false;

/** Destrava o contexto no próximo gesto do usuário (política de autoplay). */
function armGestureResume(ctx: AudioContext) {
  if (ctx.state === "running" || resumeArmed) return;
  resumeArmed = true;
  const fire = () => {
    resumeArmed = false;
    for (const ev of RESUME_EVENTS) document.removeEventListener(ev, fire);
    ctx.resume().catch(() => {});
  };
  for (const ev of RESUME_EVENTS)
    document.addEventListener(ev, fire, { passive: true });
}

// createMediaElementSource só pode acontecer UMA vez por elemento na vida da
// página — a segunda chamada lança InvalidStateError. O node fica cacheado e
// a conexão node→destination é permanente (é a nova rota de saída do player).
const elementSources = new WeakMap<HTMLAudioElement, MediaElementAudioSourceNode>();

function wireElementSource(
  ctx: AudioContext,
  el: HTMLAudioElement,
): MediaElementAudioSourceNode | null {
  try {
    let node = elementSources.get(el);
    if (!node) {
      node = ctx.createMediaElementSource(el);
      node.connect(ctx.destination);
      elementSources.set(el, node);
    }
    return node;
  } catch (err) {
    // Elemento já capturado por outro contexto (ou WebKit recusou) — o orb
    // segue no envelope sintético; o áudio do player não é tocado.
    console.warn("[VyvEnergy] createMediaElementSource indisponível — envelope sintético", err);
    return null;
  }
}

// ── Envelopes sintéticos ────────────────────────────────────────────────────

/** Respiração do thinking: 0.10..0.26, período 3.5s. */
function breathLevel(nowMs: number): number {
  return 0.1 + 0.16 * (0.5 + 0.5 * Math.sin((nowMs / 1000) * ((2 * Math.PI) / 3.5)));
}

/** Cadência de fala: sílabas ~1.8Hz moduladas por frases, com pausas. */
function speechLevel(nowMs: number): number {
  const t = nowMs / 1000;
  const syllable = Math.pow(0.5 + 0.5 * Math.sin(t * 11.0 + Math.sin(t * 3.1) * 2.0), 2);
  const phrase = Math.max(0, 0.55 + 0.45 * Math.sin(t * 0.9 + Math.sin(t * 0.23) * 1.3));
  return clamp01(0.1 + 0.72 * syllable * phrase);
}

// Presença mínima em CSS puro para quando o WebGL não existe: disco escuro na
// paleta com núcleo ciano respirando (mesmo período 3.5s do thinking). Zero
// dependências; color-mix aceita a paleta em qualquer formato de cor (as CSS
// vars --vyv-energy-* podem vir em hsl/rgb).
function EnergyFallback({
  size,
  palette,
  animate,
}: {
  size: number;
  palette: { bg: string; anchor: string; a: string; b: string; c: string };
  animate: boolean;
}) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        position: "relative",
        overflow: "hidden",
        background:
          `radial-gradient(circle at 50% 42%, ` +
          `color-mix(in srgb, ${palette.c} 16%, transparent) 0%, ` +
          `color-mix(in srgb, ${palette.b} 12%, transparent) 34%, ` +
          `${palette.bg} 78%)`,
        boxShadow: `inset 0 0 ${Math.round(size * 0.16)}px color-mix(in srgb, ${palette.anchor} 45%, transparent)`,
      }}
    >
      <style>{`@keyframes vyv-energy-breath { 0%, 100% { opacity: 0.55; transform: scale(0.94); } 50% { opacity: 0.95; transform: scale(1.06); } }`}</style>
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: "38%",
          height: "38%",
          marginLeft: "-19%",
          marginTop: "-19%",
          borderRadius: "50%",
          background:
            `radial-gradient(circle, ` +
            `color-mix(in srgb, ${palette.b} 55%, transparent) 0%, ` +
            `color-mix(in srgb, ${palette.a} 22%, transparent) 55%, ` +
            `transparent 75%)`,
          animation: animate ? "vyv-energy-breath 3.5s ease-in-out infinite" : "none",
        }}
      />
    </div>
  );
}

export default function VyvEnergy({
  state,
  size,
  ratio = 1,
  source = null,
  level,
  interactive = true,
  className,
  style,
}: VyvEnergyProps) {
  const { t } = useLanguage();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [measured, setMeasured] = useState(0);
  const [liveLevel, setLiveLevel] = useState(0);

  // Fallback sem WebGL: sonda na montagem + falha de init reportada pelo orb
  // (patch 4). Nunca deixar o usuário sem avatar com um canvas vazio.
  const webglOk = useMemo(hasWebGL, []);
  const [orbFailed, setOrbFailed] = useState(false);
  const showFallback = !webglOk || orbFailed;

  // Lido uma vez por montagem: com reduce ligado o orb congela num frame
  // (patch 3 do cosmic-orb), então análise de áudio aqui seria só bateria.
  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true,
    [],
  );

  const palette = useMemo(
    () => ({
      bg: cssVar("--vyv-energy-bg", FALLBACK_PALETTE.bg),
      anchor: cssVar("--vyv-energy-anchor", FALLBACK_PALETTE.anchor),
      a: cssVar("--vyv-energy-a", FALLBACK_PALETTE.a),
      b: cssVar("--vyv-energy-b", FALLBACK_PALETTE.b),
      c: cssVar("--vyv-energy-c", FALLBACK_PALETTE.c),
    }),
    [],
  );

  // Sem `size` explícito, o diâmetro é o lado menor do container.
  useEffect(() => {
    if (typeof size === "number") return;
    const el = rootRef.current;
    if (!el) return;
    const apply = () =>
      setMeasured(Math.max(0, Math.floor(Math.min(el.clientWidth, el.clientHeight))));
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    apply();
    return () => ro.disconnect();
  }, [size]);

  const hasDirectLevel =
    typeof level === "number" && (state === "listening" || state === "speaking");

  const needsLoop =
    !reducedMotion &&
    !showFallback &&
    !hasDirectLevel &&
    (state === "thinking" ||
      state === "speaking" ||
      (state === "listening" && source instanceof MediaStream));

  useEffect(() => {
    if (!needsLoop) {
      setLiveLevel(0);
      return;
    }

    let disposed = false;
    let analyser: AnalyserNode | null = null;
    let buf: Uint8Array | null = null;
    let streamSrc: MediaStreamAudioSourceNode | null = null;
    let elementNode: MediaElementAudioSourceNode | null = null;
    // Ganho do RMS: 6 para mic (validado no vyv-orb-preview), 4 para TTS
    // (playback normalizado satura antes).
    let gain = 6;

    const attachStream = (ctx: AudioContext, stream: MediaStream, streamGain: number) => {
      if (disposed) return;
      try {
        streamSrc = ctx.createMediaStreamSource(stream);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        streamSrc.connect(analyser);
        buf = new Uint8Array(analyser.frequencyBinCount);
        gain = streamGain;
      } catch (err) {
        console.warn("[VyvEnergy] análise do MediaStream falhou", err);
      }
    };

    const attachElement = (ctx: AudioContext, el: HTMLAudioElement) => {
      if (disposed || ctx.state !== "running") return;
      const node = wireElementSource(ctx, el);
      if (!node) return;
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      node.connect(analyser);
      elementNode = node;
      buf = new Uint8Array(analyser.frequencyBinCount);
      gain = 4;
    };

    if ((state === "listening" || state === "speaking") && source instanceof MediaStream) {
      // Mic (listening) ou voz remota do Realtime (speaking). 6 para o mic,
      // 4 para a voz remota — playback normalizado satura antes.
      const streamGain = state === "listening" ? 6 : 4;
      const ctx = getSharedCtx();
      if (ctx) {
        armGestureResume(ctx);
        if (ctx.state === "running") attachStream(ctx, source, streamGain);
        else ctx.resume().then(() => attachStream(ctx, source, streamGain)).catch(() => {});
      }
    } else if (
      state === "speaking" &&
      source instanceof HTMLAudioElement &&
      !IS_IOS_WEBKIT
    ) {
      const ctx = getSharedCtx();
      if (ctx) {
        armGestureResume(ctx);
        if (ctx.state === "running") attachElement(ctx, source);
        else ctx.resume().then(() => attachElement(ctx, source)).catch(() => {});
      }
    }

    const id = window.setInterval(() => {
      if (disposed) return;
      const now = performance.now();
      let v = 0;
      if (analyser && buf) {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const s = (buf[i] - 128) / 128;
          sum += s * s;
        }
        v = clamp01(Math.sqrt(sum / buf.length) * gain);
      } else if (state === "thinking") {
        v = breathLevel(now);
      } else if (state === "speaking") {
        const el = source instanceof HTMLAudioElement ? source : null;
        const playing = el ? !el.paused && !el.ended : true;
        v = playing ? speechLevel(now) : 0;
      }
      setLiveLevel(v);
    }, 40);

    return () => {
      disposed = true;
      window.clearInterval(id);
      if (streamSrc) streamSrc.disconnect();
      // Só a ponta do analyser — a conexão node→destination fica (é a rota
      // de saída do player agora).
      if (elementNode && analyser) elementNode.disconnect(analyser);
      analyser = null;
      buf = null;
    };
  }, [needsLoop, state, source]);

  // `ratio` fora de (0..1] não estreita nada — só quebraria o enquadramento
  // em silêncio. Clampa e segue.
  const safeRatio = Number.isFinite(ratio) ? Math.min(1, Math.max(0.05, ratio)) : 1;
  const diameter =
    typeof size === "number" ? size : Math.floor(measured * safeRatio);
  const orbAudio = reducedMotion
    ? 0
    : hasDirectLevel
      ? clamp01(level as number)
      : needsLoop
        ? liveLevel
        : 0;

  return (
    <div
      ref={rootRef}
      className={className}
      role="img"
      aria-label={t("mirror.energy.aria_label")}
      style={{
        width: typeof size === "number" ? size : "100%",
        height: typeof size === "number" ? size : "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      {diameter > 0 &&
        (showFallback ? (
          <EnergyFallback size={diameter} palette={palette} animate={!reducedMotion} />
        ) : (
          <Orb
            aria-hidden="true"
            size={diameter}
            background={palette.bg}
            palette={{
              anchor: palette.anchor,
              colorA: palette.a,
              colorB: palette.b,
              colorC: palette.c,
            }}
            lens={diameter <= LENS_MAX_DIAMETER}
            audio={orbAudio}
            interactive={interactive && !reducedMotion}
            onInitError={() => setOrbFailed(true)}
          />
        ))}
    </div>
  );
}
